import { createFileRoute, Link } from "@tanstack/react-router";
import { useLoaderData } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";

const BASE = "https://sellavo.com";
const TITLE = "Jämför sajter för att sälja begagnat | Sellavo";
const DESCRIPTION =
  "Blocket, Tradera, Facebook Marketplace eller Sellpy? Jämför hur tjänsterna fungerar, vad de kostar i stora drag och när Sellavo passar bättre.";

export const Route = createFileRoute("/jamfor/")({
  loader: async () => {
    const { COMPARISONS } = await import("@/lib/jamforelser");
    return {
      items: COMPARISONS.map((c) => ({ slug: c.slug, h1: c.h1, description: c.description })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${BASE}/jamfor` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `${BASE}/jamfor` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Jämförelser av sajter för att sälja begagnat",
          itemListElement: (loaderData?.items ?? []).map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.h1,
            url: `${BASE}/jamfor/${c.slug}`,
          })),
        }),
      },
    ],
  }),
  component: CompareHub,
});

export function CompareHub() {
  const { items } = useLoaderData({ strict: false }) as { items: { slug: string; h1: string; description: string }[] };
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).compare;
  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <p className="font-heading text-[11px] font-bold uppercase tracking-wide text-primary">
        {copy?.eyebrow ?? "Jämförelser"}
      </p>
      <h1 className="mt-2 font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        {copy?.title ?? "Var ska du sälja dina prylar?"}
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
        {copy?.intro ?? "Tjänsterna fungerar olika: några har mest trafik, några har budgivning och några hämtar sakerna hos dig. Här går vi igenom skillnaderna rakt upp och ner – även när någon annan passar bättre än vi gör."}
      </p>

      <ul className="mt-10 grid gap-4 sm:grid-cols-2">
        {items.map((c) => (
          <li key={c.slug}>
            <Link
              to="/jamfor/$tjanst"
              params={{ tjanst: c.slug }}
              className="flex h-full flex-col rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary/50 sm:p-6"
            >
              <h2 className="font-heading text-lg font-bold text-foreground">{c.h1}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                {copy?.read ?? "Läs jämförelsen"}
                <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
